import { useCallback, useState } from "react";

import { fetchFastApi } from "../api/fastapi";
import { useHookCache } from "../lib/hookCache";
import { trailCacheKeys } from "./trailCacheKeys";
import { type AuditSession } from "../models/audit/auditSession";
import { type Citation } from "../models/audit/citation";
import { type ProcedureSessionTableShell } from "../models/audit/procedureSessionTableShell";
import { type LinkingData } from "../pages/extraction/viewExtraction/PdfViewer";
import { type BoundingBoxState } from "../pages/audit/auditWorkPaperViewV2/components/attributeTestAnalysis/types";
import { type UploadCitationResponse } from "../pages/audit/auditWorkPaperViewV2/components/AddCitationModal";

interface UseTrailAddCitationProps {
  auditSession: AuditSession;
  attributeTestResult:
    | AuditSession["sourceRows"][number]["attributeTestResults"][number]
    | undefined;
  sourceRowId?: string;
  allCitations: Citation[];
  setCurrentBoundingBox: (box: BoundingBoxState | undefined) => void;
}

/**
 * Add-citation flow for the auditor trail's three-pane fold-out. The "Add
 * Citation" button above the doc viewer opens the shared `AddCitationModal` in
 * trail mode; the user picks which L2 `auditor_step` the citation attaches to
 * from the in-dialog step picker (no reperformance step / L2-group pickers, and
 * the new citation carries only `auditor_step_id`).
 *
 * Trail citations live under `auditorTrail.steps[].citations`, a cache shape the
 * reperformance/inline optimistic writers don't touch, so both handlers POST and
 * then invalidate the attribute-test-result (and its source row) to surface the
 * new citation rather than threading the trail shape through every writer.
 */
export function useTrailAddCitation({
  auditSession,
  attributeTestResult,
  sourceRowId,
  allCitations,
  setCurrentBoundingBox,
}: UseTrailAddCitationProps) {
  const { setEntry, invalidate } = useHookCache();
  const [open, setOpen] = useState(false);

  const invalidateTrailCaches = useCallback(() => {
    if (!attributeTestResult) return;
    trailCacheKeys(
      auditSession.id,
      attributeTestResult.id ?? "",
      sourceRowId,
    ).forEach((key) => void invalidate(key));
  }, [invalidate, attributeTestResult, auditSession.id, sourceRowId]);

  // Search-tab flow: a bounding box was drawn on an already-loaded document, so
  // POST the citation against the known auditor step, then refetch the trail.
  const handleCreateCitation = useCallback(
    async (data: {
      summary: string;
      linkingData: LinkingData;
      documentId: string;
      pageWidth?: number;
      pageHeight?: number;
      isKey?: boolean;
      auditorStepId?: string | null;
    }) => {
      const stepId = data.auditorStepId;
      if (!attributeTestResult?.id || !stepId) {
        throw new Error("Missing required data for citation creation");
      }

      const newCitationId = crypto.randomUUID();
      const now = new Date().toISOString();
      const encodedCitationString = `pdf-manual#${data.documentId}`;

      await fetchFastApi({
        method: "POST",
        path: `/citations`,
        body: {
          citation: {
            id: newCitationId,
            document_id: data.documentId,
            encoded_citation_string: encodedCitationString,
            short_summary: data.summary,
            bounding_box: data.linkingData.boundingBox,
            page_number: data.linkingData.pageNumber,
            page_width: data.pageWidth ?? null,
            page_height: data.pageHeight ?? null,
            citation_order: (allCitations.length || 0) + 1,
            is_key: data.isKey ?? true,
            auditor_step_id: stepId,
            created_at: now,
            updated_at: now,
          },
        },
      });

      invalidateTrailCaches();
      setCurrentBoundingBox({
        boundingBox: data.linkingData.boundingBox,
        pageNumber: data.linkingData.pageNumber,
        pageWidth: data.pageWidth ?? undefined,
        pageHeight: data.pageHeight ?? undefined,
        elementId: encodedCitationString,
        citationId: newCitationId,
      });
    },
    [
      attributeTestResult?.id,
      allCitations.length,
      invalidateTrailCaches,
      setCurrentBoundingBox,
    ],
  );

  // Upload-tab flow: the modal already POSTed the citation against the auditor
  // step and uploaded the document. Add the fresh document to the table-shell
  // cache so its presigned URL resolves, then refetch the trail.
  const handleUploadCreated = useCallback(
    (response: UploadCitationResponse) => {
      if (!attributeTestResult?.id || !response.citation.auditorStepId) return;

      const { document } = response;
      const sessionDocument = {
        id: response.sessionDocument?.id ?? document.id,
        documentId: document.id,
        name: document.fileName,
        fetchableDocument: {
          id: document.id,
          fileName: document.fileName,
          fileType: document.fileType,
          fileUrl: document.fileUrl,
          fileContentHash: document.fileContentHash,
          documentAssignments: null,
          extra: document.extra,
          parentDocumentId: document.parentDocumentId,
          childType: null,
          state: "ready" as const,
          isParsed: document.isParsed,
          createdAt: document.createdAt,
          updatedAt: document.updatedAt,
        },
        creatorType: "user" as const,
        state: "approved" as const,
        extraction: null,
        extractions: [],
        // A file uploaded as a citation is not an analytics input until the
        // auditor marks it as one.
        isAnalyticsInput: false,
      };

      setEntry<ProcedureSessionTableShell>(
        ["procedure-session", auditSession.id, "table-shell"],
        (prev) => ({
          ...prev,
          documents: [
            ...prev.documents,
            { ...sessionDocument, sampleNumbers: [] },
          ],
        }),
      );
      invalidateTrailCaches();
    },
    [auditSession.id, attributeTestResult?.id, setEntry, invalidateTrailCaches],
  );

  return {
    open,
    setOpen,
    handleCreateCitation,
    handleUploadCreated,
  };
}
